import type {
  OpenPencilFigmaSourceBundle,
  OpenPencilSurfacePreviewArtifact,
} from "./openpencil-figma-source";
import { loadOpenPencilSurfacePreview } from "./openpencil-figma-client";

export const OPENPENCIL_REVISION_VISUAL_DIFF_SCHEMA =
  "cogo.studio.openpencil-revision-visual-diff.v1" as const;
export const OPENPENCIL_REVISION_VISUAL_DIFF_RUNNER =
  "cogo-studio-browser-pixel-diff/1.0.0" as const;

export interface OpenPencilDecodedPreview {
  width: number;
  height: number;
  data: Uint8ClampedArray;
}

export interface OpenPencilRevisionSurfaceDiff {
  surfaceId: string;
  status: "unchanged" | "changed" | "blocked";
  beforeArtifactRef: string | null;
  afterArtifactRef: string | null;
  beforeSha256: string | null;
  afterSha256: string | null;
  width: number | null;
  height: number | null;
  changedPixels: number | null;
  totalPixels: number | null;
  diffRatio: number | null;
  blocker?: string;
}

export interface OpenPencilRevisionVisualDiff {
  schemaVersion: typeof OPENPENCIL_REVISION_VISUAL_DIFF_SCHEMA;
  runner: typeof OPENPENCIL_REVISION_VISUAL_DIFF_RUNNER;
  parentSourceId: string;
  sourceId: string;
  sourceRevision: number;
  patchId: string;
  status: "approved" | "pending" | "blocked";
  disposition: "no-change" | "pending" | "human-approved" | "gemma-approved";
  changedSurfaceIds: string[];
  surfaces: OpenPencilRevisionSurfaceDiff[];
  evidenceRefs: string[];
  blockers: string[];
  pixelThreshold: number;
  evaluatedAt: string;
  judgementId?: string;
}

interface CompareOptions {
  loadPreview?: (
    bundle: OpenPencilFigmaSourceBundle,
    preview: OpenPencilSurfacePreviewArtifact,
  ) => Promise<Blob>;
  decodePreview?: (blob: Blob) => Promise<OpenPencilDecodedPreview>;
  pixelThreshold?: number;
  concurrency?: number;
  evaluatedAt?: string;
}

export function compareDecodedOpenPencilPreviews(
  before: OpenPencilDecodedPreview,
  after: OpenPencilDecodedPreview,
  pixelThreshold = 16,
): Pick<
  OpenPencilRevisionSurfaceDiff,
  "width" | "height" | "changedPixels" | "totalPixels" | "diffRatio"
> {
  const width = Math.max(before.width, after.width);
  const height = Math.max(before.height, after.height);
  const totalPixels = width * height;
  let changedPixels = 0;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (
        x >= before.width ||
        y >= before.height ||
        x >= after.width ||
        y >= after.height
      ) {
        changedPixels += 1;
        continue;
      }
      const beforeIndex = (y * before.width + x) * 4;
      const afterIndex = (y * after.width + x) * 4;
      if (
        Math.abs(before.data[beforeIndex] - after.data[afterIndex]) >
          pixelThreshold ||
        Math.abs(before.data[beforeIndex + 1] - after.data[afterIndex + 1]) >
          pixelThreshold ||
        Math.abs(before.data[beforeIndex + 2] - after.data[afterIndex + 2]) >
          pixelThreshold ||
        Math.abs(before.data[beforeIndex + 3] - after.data[afterIndex + 3]) >
          pixelThreshold
      ) {
        changedPixels += 1;
      }
    }
  }
  return {
    width,
    height,
    changedPixels,
    totalPixels,
    diffRatio: totalPixels === 0 ? 0 : changedPixels / totalPixels,
  };
}

async function decodePreviewBlob(
  blob: Blob,
): Promise<OpenPencilDecodedPreview> {
  const bitmap = await createImageBitmap(blob);
  try {
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) throw new Error("Canvas 2D context is unavailable.");
    context.drawImage(bitmap, 0, 0);
    return {
      width: bitmap.width,
      height: bitmap.height,
      data: context.getImageData(0, 0, bitmap.width, bitmap.height).data,
    };
  } finally {
    bitmap.close();
  }
}

async function mapConcurrent<T, R>(
  values: T[],
  concurrency: number,
  worker: (value: T) => Promise<R>,
): Promise<R[]> {
  const output = new Array<R>(values.length);
  let nextIndex = 0;
  await Promise.all(
    Array.from({ length: Math.min(concurrency, values.length) }, async () => {
      while (nextIndex < values.length) {
        const index = nextIndex;
        nextIndex += 1;
        output[index] = await worker(values[index]);
      }
    }),
  );
  return output;
}

export async function compareOpenPencilRevisionVisuals(
  beforeBundle: OpenPencilFigmaSourceBundle,
  afterBundle: OpenPencilFigmaSourceBundle,
  options: CompareOptions = {},
): Promise<OpenPencilRevisionVisualDiff> {
  const lineage = afterBundle.revisionLineage;
  if (
    !lineage ||
    lineage.parentSourceId !== beforeBundle.sourceId ||
    lineage.parentSha256 !== beforeBundle.fileSha256
  ) {
    throw new Error("Revision visual diff requires verified parent lineage.");
  }
  const beforeItems = new Map(
    (beforeBundle.visualPreviewManifest?.items ?? []).map((item) => [
      item.surfaceId,
      item,
    ]),
  );
  const afterItems = new Map(
    (afterBundle.visualPreviewManifest?.items ?? []).map((item) => [
      item.surfaceId,
      item,
    ]),
  );
  const surfaceIds = [
    ...new Set([...beforeItems.keys(), ...afterItems.keys()]),
  ].sort();
  const pixelThreshold = options.pixelThreshold ?? 16;
  const loadPreview = options.loadPreview ?? loadOpenPencilSurfacePreview;
  const decodePreview = options.decodePreview ?? decodePreviewBlob;
  const surfaces = await mapConcurrent(
    surfaceIds,
    Math.max(1, options.concurrency ?? 4),
    async (surfaceId): Promise<OpenPencilRevisionSurfaceDiff> => {
      const before = beforeItems.get(surfaceId);
      const after = afterItems.get(surfaceId);
      const base = {
        surfaceId,
        beforeArtifactRef: before?.artifactRef ?? null,
        afterArtifactRef: after?.artifactRef ?? null,
        beforeSha256: before?.sha256 ?? null,
        afterSha256: after?.sha256 ?? null,
      };
      if (!before || !after) {
        return {
          ...base,
          status: "blocked",
          width: null,
          height: null,
          changedPixels: null,
          totalPixels: null,
          diffRatio: null,
          blocker: "The revision preview set does not match its parent.",
        };
      }
      if (before.sha256 === after.sha256) {
        return {
          ...base,
          status: "unchanged",
          width: after.renderedWidth,
          height: after.renderedHeight,
          changedPixels: 0,
          totalPixels: after.renderedWidth * after.renderedHeight,
          diffRatio: 0,
        };
      }
      try {
        const [beforePixels, afterPixels] = await Promise.all([
          loadPreview(beforeBundle, before).then(decodePreview),
          loadPreview(afterBundle, after).then(decodePreview),
        ]);
        const diff = compareDecodedOpenPencilPreviews(
          beforePixels,
          afterPixels,
          pixelThreshold,
        );
        return {
          ...base,
          status: diff.changedPixels === 0 ? "unchanged" : "changed",
          ...diff,
        };
      } catch (error) {
        return {
          ...base,
          status: "blocked",
          width: null,
          height: null,
          changedPixels: null,
          totalPixels: null,
          diffRatio: null,
          blocker:
            error instanceof Error
              ? error.message
              : "Revision preview comparison failed.",
        };
      }
    },
  );
  const blockers = surfaces
    .filter((surface) => surface.status === "blocked")
    .map(
      (surface) =>
        surface.surfaceId + ": " + (surface.blocker ?? "Visual diff blocked."),
    );
  const changedSurfaceIds = surfaces
    .filter((surface) => surface.status === "changed")
    .map((surface) => surface.surfaceId);
  const evidenceRefs = [
    ...new Set(
      surfaces.flatMap((surface) =>
        [surface.beforeArtifactRef, surface.afterArtifactRef].filter(
          (value): value is string => Boolean(value),
        ),
      ),
    ),
  ];
  return {
    schemaVersion: OPENPENCIL_REVISION_VISUAL_DIFF_SCHEMA,
    runner: OPENPENCIL_REVISION_VISUAL_DIFF_RUNNER,
    parentSourceId: beforeBundle.sourceId,
    sourceId: afterBundle.sourceId,
    sourceRevision: lineage.sourceRevision,
    patchId: lineage.patchId,
    status:
      blockers.length > 0
        ? "blocked"
        : changedSurfaceIds.length > 0
          ? "pending"
          : "approved",
    disposition: changedSurfaceIds.length > 0 ? "pending" : "no-change",
    changedSurfaceIds,
    surfaces,
    evidenceRefs,
    blockers,
    pixelThreshold,
    evaluatedAt: options.evaluatedAt ?? new Date().toISOString(),
  };
}
